/* eslint-disable solid/no-innerhtml */

import {
  createEffect,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from 'solid-js'
import { debounce } from '../../utilities'
import {
  ACTIVE_CLASS,
  MENU_OPEN_CLASS,
  SEARCH_OPEN_CLASS,
  SELECTED_CLASS,
} from '../../utilities/constants'

function isOutsideViewportRight(el) {
  return el.getBoundingClientRect().right > window.innerWidth
}

export default function LargeNavigation(props) {
  let selectedTimer
  let dropdownTimer
  let enterTimer
  let locked = true

  const searchInputSelector = '.js-headSearchInput'
  const subDropdownSelectors = '.js-navDropdownSub, .js-navDropdownSubsub'
  const topDropdownSelector = '.js-navDropdownTop'
  const topItemSelector = '.js-navItemTop'
  const OUTSIDE_CLASS = 'is-outside'

  const [dropdownActive, setDropdownActive] = createSignal(false)
  const [dropdownHeight, setDropdownHeight] = createSignal(0)
  const [selectedTop, setSelectedTop] = createSignal(null)
  const [selectedSub, setSelectedSub] = createSignal(null)
  const [selectedSubSub, setSelectedSubSub] = createSignal(null)

  function handleDropdownEnter() {
    clearTimeout(selectedTimer)
    clearTimeout(dropdownTimer)

    setDropdownActive(true)
  }

  function handleDropdownLeave() {
    clearTimeout(dropdownTimer)
    clearTimeout(selectedTimer)

    dropdownTimer = setTimeout(() => {
      setSelectedTop(null)
      setSelectedSub(null)
      setSelectedSubSub(null)
      setDropdownActive(false)
    }, 300)
  }

  function handleItemMouseEnter(
    itemIndex,
    subItemId = null,
    subSubItemId = null,
    hasChildren,
  ) {
    if (locked) return

    clearTimeout(dropdownTimer)
    clearTimeout(selectedTimer)

    enterTimer = setTimeout(() => {
      if (hasChildren || subItemId) {
        setSelectedTop(itemIndex)
        setSelectedSub(subItemId)
        setSelectedSubSub(subSubItemId)
        setDropdownActive(true)
      } else {
        selectedTimer = setTimeout(() => {
          setSelectedTop(null)
          setSelectedSub(null)
          setSelectedSubSub(null)
          setDropdownActive(false)
        }, 500)
      }
    }, 40)
  }

  const handleItemMouseLeave = () => {
    clearTimeout(enterTimer)
  }

  const handleSearchClick = () => {
    setSelectedTop(null)
    setSelectedSub(null)
    setSelectedSubSub(null)
    setDropdownActive(false)

    const isActive =
      document.documentElement.classList.toggle(SEARCH_OPEN_CLASS)

    if (isActive) {
      const searchInputEl = document.body.querySelector(searchInputSelector)

      if (searchInputEl) {
        searchInputEl.focus({
          preventScroll: true,
        })
      }
    }
  }

  function handleDropdownsOutside() {
    Array.from(props.container.querySelectorAll(subDropdownSelectors)).forEach(
      (el) => el.classList.toggle(OUTSIDE_CLASS, isOutsideViewportRight(el)),
    )
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      setDropdownActive(false)
      setSelectedTop(null)
      setSelectedSub(null)
      setSelectedSubSub(null)
    }
  }

  onCleanup(() => {
    clearTimeout(dropdownTimer)
    clearTimeout(selectedTimer)

    document.documentElement.classList.remove(MENU_OPEN_CLASS)
    document.documentElement.classList.remove(SEARCH_OPEN_CLASS)
  })

  onMount(() => {
    window.addEventListener('visibilitychange', handleVisibilityChange)

    window.addEventListener(
      'mousemove',
      () => {
        locked = false
      },
      {
        once: true,
        passive: true,
      },
    )
  })

  createEffect(() => {
    handleDropdownsOutside()

    const debouncedResize = debounce(handleDropdownsOutside, 500)

    window.addEventListener('resize', debouncedResize)

    onCleanup(() => window.removeEventListener('resize', debouncedResize))
  })

  onCleanup(() => {
    window.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  createEffect(() => {
    document.documentElement.classList.toggle(MENU_OPEN_CLASS, dropdownActive())
  })

  createEffect(() => {
    if (selectedTop() !== null) {
      const topItem = props.container.querySelector(
        `${topItemSelector}:nth-child(${selectedTop() + 1})`,
      )
      const topDropdownEl = topItem.querySelector(topDropdownSelector)

      const topDropdownRect = topDropdownEl.getBoundingClientRect()
      let topPos = topDropdownRect.top
      let bottomPos = topDropdownRect.bottom

      if (selectedSub() !== null) {
        const subDropdownEl = topDropdownEl.querySelector(
          `#sub-${selectedSub()}`,
        )

        if (subDropdownEl) {
          const subDropdownRect = subDropdownEl.getBoundingClientRect()
          topPos = Math.min(topPos, subDropdownRect.top)
          bottomPos = Math.max(bottomPos, subDropdownRect.bottom)

          if (selectedSubSub() !== null) {
            const subSubDropdownEl = subDropdownEl.querySelector(
              `#subsub-${selectedSubSub()}`,
            )

            if (subSubDropdownEl) {
              const subSubDropdownRect =
                subSubDropdownEl.getBoundingClientRect()

              topPos = Math.min(topPos, subSubDropdownRect.top)
              bottomPos = Math.max(bottomPos, subSubDropdownRect.bottom)
            }
          }
        }
      }

      setDropdownHeight(bottomPos - topPos)
    } else {
      setDropdownHeight(0)
    }
  })

  return (
    <div class="LargeNavigation" onMouseLeave={handleDropdownLeave}>
      <div class="LargeNavigation-inner">
        <ul class="LargeNavigation-list">
          <For each={props?.data?.main}>
            {(item, itemIndex) => {
              const itemChildren = item?.children.reduce((acc, child) => {
                acc.push(child.title === item.title ? child.children : child)

                return acc
              }, [])

              return (
                <li
                  classList={{
                    'LargeNavigation-item': true,
                    'LargeNavigation-item--top': true,
                    [ACTIVE_CLASS]: window.location.href === item.url,
                    [SELECTED_CLASS]: selectedTop() === itemIndex(),
                    'js-navItemTop': true,
                  }}
                  onMouseEnter={() =>
                    handleItemMouseEnter(
                      itemIndex(),
                      null,
                      null,
                      Boolean(itemChildren.length),
                    )
                  }
                  onMouseLeave={handleItemMouseLeave}
                >
                  <div class="LargeNavigation-itemInner">
                    <a
                      class="LargeNavigation-itemLink"
                      href={item.url}
                      aria-expanded={dropdownActive() ? 'true' : 'false'}
                      onClick={(e) => {
                        if (itemChildren.length && !dropdownActive()) {
                          e.preventDefault()

                          handleItemMouseEnter(
                            itemIndex(),
                            null,
                            null,
                            Boolean(itemChildren.length),
                          )
                        }
                      }}
                      innerHTML={item.title}
                    />
                  </div>
                  <Show when={itemChildren.length}>
                    <div class="LargeNavigation-itemDropdown js-navDropdownTop">
                      <ul class="LargeNavigation-itemDropdownInner">
                        <For each={itemChildren}>
                          {(subItem) => {
                            const subItemChildren = subItem?.children

                            return (
                              <li
                                classList={{
                                  'LargeNavigation-item': true,
                                  'LargeNavigation-item--sub': true,
                                  [SELECTED_CLASS]:
                                    selectedSub() === subItem.id,
                                  [ACTIVE_CLASS]:
                                    window.location.href === subItem.url,
                                  'js-navDropdownSub': true,
                                }}
                                onMouseEnter={() =>
                                  handleItemMouseEnter(
                                    itemIndex(),
                                    subItem.id,
                                    null,
                                    Boolean(subItemChildren?.length),
                                  )
                                }
                                onMouseLeave={handleItemMouseLeave}
                              >
                                <div class="LargeNavigation-itemInner">
                                  <a
                                    class="LargeNavigation-itemLink"
                                    href={subItem.url}
                                    innerHTML={subItem.title}
                                    aria-expanded={selectedSub() === subItem.id}
                                    onClick={(e) => {
                                      if (
                                        itemChildren.length &&
                                        selectedSub() !== subItem.id
                                      ) {
                                        e.preventDefault()

                                        handleItemMouseEnter(
                                          itemIndex(),
                                          null,
                                          null,
                                          Boolean(itemChildren.length),
                                        )
                                      }
                                    }}
                                  />
                                  <Show when={subItemChildren?.length}>
                                    <svg
                                      class="LargeNavigation-itemButtonSymbol Button-symbol"
                                      width="16"
                                      height="10"
                                      aria-hidden="true"
                                    >
                                      <use xlink:href="#symbol-chevron-down" />
                                    </svg>
                                  </Show>
                                </div>
                                <Show when={subItemChildren?.length}>
                                  <div
                                    class="LargeNavigation-itemDropdown LargeNavigation-itemDropdown--sub"
                                    id={`sub-${subItem.id}`}
                                  >
                                    <ul class="LargeNavigation-itemDropdownInner">
                                      <For each={subItemChildren}>
                                        {(subItemChild) => {
                                          const subSubItemChildren =
                                            subItemChild?.children

                                          return (
                                            <li
                                              classList={{
                                                'LargeNavigation-item': true,
                                                'LargeNavigation-item--sub': true,
                                                [SELECTED_CLASS]:
                                                  selectedSubSub() ===
                                                  subItemChild.id,
                                                [ACTIVE_CLASS]:
                                                  window.location.href ===
                                                  subItemChild.url,
                                              }}
                                              onMouseEnter={() =>
                                                handleItemMouseEnter(
                                                  itemIndex(),
                                                  subItem.id,
                                                  subItemChild.id,
                                                  Boolean(
                                                    subSubItemChildren?.length,
                                                  ),
                                                )
                                              }
                                              onMouseLeave={
                                                handleItemMouseLeave
                                              }
                                            >
                                              <div class="LargeNavigation-itemInner">
                                                <a
                                                  class="LargeNavigation-itemLink"
                                                  href={subItemChild.url}
                                                  innerHTML={subItemChild.title}
                                                />
                                                <Show
                                                  when={
                                                    subSubItemChildren?.length
                                                  }
                                                >
                                                  <svg
                                                    class="LargeNavigation-itemButtonSymbol Button-symbol"
                                                    width="16"
                                                    height="10"
                                                    aria-hidden="true"
                                                  >
                                                    <use xlink:href="#symbol-chevron-down" />
                                                  </svg>
                                                </Show>
                                              </div>
                                              <Show
                                                when={
                                                  subSubItemChildren?.length
                                                }
                                              >
                                                <div
                                                  class="LargeNavigation-itemDropdown js-navDropdownSubsub"
                                                  id={`subsub-${subItemChild.id}`}
                                                >
                                                  <ul class="LargeNavigation-itemDropdownInner">
                                                    <For
                                                      each={subSubItemChildren}
                                                    >
                                                      {(subSubItemChild) => {
                                                        return (
                                                          <li
                                                            classList={{
                                                              'LargeNavigation-item': true,
                                                              'LargeNavigation-item--sub': true,
                                                              [ACTIVE_CLASS]:
                                                                window.location
                                                                  .href ===
                                                                subSubItemChild.url,
                                                            }}
                                                          >
                                                            <div class="LargeNavigation-itemInner">
                                                              <a
                                                                class="LargeNavigation-itemLink"
                                                                href={
                                                                  subSubItemChild.url
                                                                }
                                                                innerHTML={
                                                                  subSubItemChild.title
                                                                }
                                                              />
                                                            </div>
                                                          </li>
                                                        )
                                                      }}
                                                    </For>
                                                    <Show
                                                      when={
                                                        subItemChild?.buttonText
                                                      }
                                                    >
                                                      <div class="LargeNavigation-itemDropdownAction">
                                                        <a
                                                          class="LargeNavigation-itemDropdownActionButton Button Button--outline Button--sm"
                                                          href={
                                                            subItemChild.url
                                                          }
                                                        >
                                                          <span class="Button-text">
                                                            {
                                                              subItemChild.buttonText
                                                            }
                                                          </span>
                                                        </a>
                                                      </div>
                                                    </Show>
                                                  </ul>
                                                </div>
                                              </Show>
                                            </li>
                                          )
                                        }}
                                      </For>
                                      <Show when={subItem?.buttonText}>
                                        <div class="LargeNavigation-itemDropdownAction">
                                          <a
                                            class="LargeNavigation-itemDropdownActionButton Button Button--outline Button--sm"
                                            href={subItem.url}
                                          >
                                            <span class="Button-text">
                                              {subItem.buttonText}
                                            </span>
                                          </a>
                                        </div>
                                      </Show>
                                    </ul>
                                  </div>
                                </Show>
                              </li>
                            )
                          }}
                        </For>
                        <Show when={item?.buttonText}>
                          <div class="LargeNavigation-itemDropdownAction">
                            <a
                              class="LargeNavigation-itemDropdownActionButton Button Button--outline Button--sm"
                              href={item.url}
                            >
                              <span class="Button-text">{item.buttonText}</span>
                            </a>
                          </div>
                        </Show>
                      </ul>
                    </div>
                  </Show>
                </li>
              )
            }}
          </For>
        </ul>
        <button
          class="LargeNavigation-searchButton Button Button--clean js-headSearchButton"
          type="button"
          onClick={handleSearchClick}
        >
          <span class="u-hiddenVisually">Sök</span>
          <svg
            class="LargeNavigation-searchButtonSymbol Button-symbol"
            width="18"
            height="18"
            aria-hidden="true"
          >
            <use xlink:href="#symbol-search" />
          </svg>
        </button>
      </div>
      <div
        classList={{
          'LargeNavigation-dropdownBackdrop': true,
          [ACTIVE_CLASS]: dropdownActive(),
        }}
        style={{
          height: `${dropdownHeight()}px`,
        }}
        onMouseEnter={handleDropdownEnter}
        onMouseLeave={handleDropdownLeave}
      />
    </div>
  )
}
